import { useEffect, useState } from "react";
import { Download } from "lucide-react";

// Hook: detects beforeinstallprompt (Chromium) and iOS Safari standalone-capability
export function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("handy_install_dismissed_at"));
  const isStandalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone);
  const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // show banner if not installed + not dismissed recently (7 days), and either Chrome prompt ready or iOS
  const showBanner = !isStandalone && !dismissed && (deferred || isIos);

  async function install() {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    }
  }

  function dismiss() {
    localStorage.setItem("handy_install_dismissed_at", String(Date.now()));
    setDismissed(String(Date.now()));
  }

  return { showBanner, isIos, install, dismiss, canPrompt: !!deferred };
}

export default function InstallBanner() {
  const { showBanner, isIos, install, dismiss, canPrompt } = useInstallPrompt();
  if (!showBanner) return null;
  return (
    <div className="card-tactile rounded-2xl p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-up" data-testid="install-banner">
      <div className="h-10 w-10 rounded-xl bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
        <Download className="h-5 w-5 text-[color:var(--primary-ink)]" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-display font-bold text-lg tracking-tight">Install Handy</h3>
        <p className="text-sm text-[color:var(--text-muted)] mt-0.5">
          {isIos
            ? <>Open Share <span aria-hidden>⇧</span>, tap <b>Add to Home Screen</b>. Required for background dose reminders on iPhone.</>
            : <>Install as an app for faster launch, offline access, and reliable background push reminders.</>}
        </p>
      </div>
      <div className="flex gap-2">
        {canPrompt && (
          <button onClick={install} data-testid="install-banner-install" className="px-4 h-10 rounded-lg bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-semibold text-sm">
            Install
          </button>
        )}
        <button onClick={dismiss} data-testid="install-banner-dismiss" className="px-4 h-10 rounded-lg border border-[color:var(--border-subtle)] text-[color:var(--text-main)] hover:bg-[color:var(--surface-2)] font-semibold text-sm">
          Not now
        </button>
      </div>
    </div>
  );
}
